// View state as pure functions: density, column order, visibility and widths. A saved or consumer-supplied
// View may name columns the configuration no longer has, miss columns it gained, or carry widths outside a
// column's current bounds; reconcileView turns any of these into a View that fits the configuration
// without resetting the choices that still apply. Every View commit starts from the reconciled View.

import { DENSITIES } from '../config/defaults'

// vertical cell padding in CSS pixels, by density (z6-me-table-view.css applies them)
export const DENSITY_PADDING = {
  comfortable: { body: 12, header: 11 },
  compact: { body: 7, header: 8 },
}

// how far one key press moves a focused resize boundary, in CSS pixels
export const RESIZE_STEP = 16
export const RESIZE_STEP_LARGE = 32

const isActions = column => column.type === 'actions'

const lockOf = column => column.lock || null

/** A data column whose width the person may change. */
export const isResizable = column =>
  Boolean(column) && !isActions(column) && column.resizable !== false

/** A column the person may move; locked, required and actions columns stay put. */
export const isReorderable = column =>
  Boolean(column) &&
  !isActions(column) &&
  !column.required &&
  !lockOf(column) &&
  column.reorderable !== false

const canHide = column => !isActions(column) && !column.required

/** A column the person may show and hide; a locked column may be one when it is configured so. */
export const isHideable = column =>
  Boolean(column) && canHide(column) && column.hideable === true

const sizeOf = column => column.size || {}

const isFloor = floor =>
  typeof floor === 'number' && Number.isFinite(floor) && floor > 0

/**
 * The narrowest a column may render: its content floor, or its minimum (config/columns.js columnSize: a
 * declared minimum, or the readable minimum a text column keeps) when that is wider. The content floor is
 * the widest of the column's header controls with room for an ellipsis and its content that cannot
 * wrap or truncate (geometry/contentFloor.js); before anything is measured the minimum alone applies.
 */
export const effectiveMin = (column, floor) => {
  const { min } = sizeOf(column)
  const configured = typeof min === 'number' ? min : 0
  return isFloor(floor) ? Math.max(configured, Math.ceil(floor)) : configured
}

/**
 * A width clamped into the column's bounds; null for a value that is not a finite number. A content floor
 * raises the lower bound and wins over a configured maximum, since a narrower column would clip content
 * that cannot bend.
 */
export const clampWidth = (column, width, floor) => {
  if (typeof width !== 'number' || !Number.isFinite(width)) return null
  const { max } = sizeOf(column)
  let next = Math.round(width)
  if (typeof max === 'number') next = Math.min(max, next)
  return Math.max(effectiveMin(column, floor), next)
}

/** The configured initial width. */
export const initialWidth = column => {
  const { width } = sizeOf(column)
  return typeof width === 'number' ? width : null
}

/**
 * Display order for the configured columns: start-locked columns first and end-locked columns last, both in
 * configuration order; the middle keeps the saved order. A saved key the configuration lacks is dropped, and
 * a configured column the saved order lacks enters after the nearest configured column that precedes it.
 */
export const reconcileOrder = (order, columns) => {
  const keys = columns.map(column => column.key)
  const saved = Array.isArray(order) ? order : []
  const placed = saved.filter(
    (key, index) => keys.includes(key) && saved.indexOf(key) === index
  )
  keys.forEach((key, index) => {
    if (placed.includes(key)) return
    let at = 0
    for (let i = index - 1; i >= 0; i -= 1) {
      const position = placed.indexOf(keys[i])
      if (position >= 0) {
        at = position + 1
        break
      }
    }
    placed.splice(at, 0, key)
  })
  const byKey = new Map(columns.map(column => [column.key, column]))
  const edge = lock => keys.filter(key => lockOf(byKey.get(key)) === lock)
  const middle = placed.filter(key => !lockOf(byKey.get(key)))
  return edge('start').concat(middle, edge('end'))
}

/** Saved widths for known resizable columns, clamped into each column's current bounds. */
export const reconcileWidths = (widths, columns) => {
  const out = {}
  if (!widths || typeof widths !== 'object') return out
  columns.forEach(column => {
    if (!isResizable(column)) return
    if (!Object.prototype.hasOwnProperty.call(widths, column.key)) return
    const width = clampWidth(column, widths[column.key])
    if (width !== null) out[column.key] = width
  })
  return out
}

/**
 * A View that fits the configuration.
 * @param {Partial<import('../types').ViewState>} view
 * @param {import('../types').NormalizedColumn[]} columns
 * @param {import('../types').Density} [defaultDensity]
 * @returns {import('../types').ViewState}
 */
export const reconcileView = (
  view = {},
  columns = [],
  defaultDensity = 'comfortable'
) => {
  const current = view || {}
  const byKey = new Map(columns.map(column => [column.key, column]))
  const hidden = (current.hidden || []).filter(
    (key, index, all) =>
      byKey.has(key) && canHide(byKey.get(key)) && all.indexOf(key) === index
  )
  return {
    density: DENSITIES.includes(current.density)
      ? current.density
      : defaultDensity,
    order: reconcileOrder(current.order, columns),
    hidden,
    widths: reconcileWidths(current.widths, columns),
  }
}

/** The rendered data columns: visible, in display order. */
export const viewColumns = (columns, view) => {
  const reconciled = reconcileView(view, columns)
  const byKey = new Map(columns.map(column => [column.key, column]))
  return reconciled.order
    .filter(key => !reconciled.hidden.includes(key))
    .map(key => byKey.get(key))
}

/** The visible column keys in display order, as the View publishes them. */
export const viewKeys = (columns, view) =>
  viewColumns(columns, view).map(column => column.key)

/** The columns View options lists, in current order; required and actions columns never appear. */
export const listedColumns = (columns, view) => {
  const byKey = new Map(columns.map(column => [column.key, column]))
  return reconcileOrder(view && view.order, columns)
    .map(key => byKey.get(key))
    .filter(
      column => canHide(column) && (isHideable(column) || isReorderable(column))
    )
}

/** The width a column renders at: the saved width, else its configured initial width. */
export const widthOf = (column, view) => {
  const saved = view && view.widths && view.widths[column.key]
  const width = clampWidth(column, saved)
  return width === null ? initialWidth(column) : width
}

/**
 * The width a column renders at: widthOf, raised to its content floor. The saved View keeps its own value;
 * only a resize writes a new one.
 */
export const renderedWidth = (column, view, floor) => {
  const width = widthOf(column, view)
  return typeof width === 'number' ? clampWidth(column, width, floor) : width
}

/**
 * Spare table width, given to columns whose header label or truncating value shows truncated, before any
 * other column. A candidate is a resizable column at its configured width (no saved width: a width the
 * person set is theirs) that renders narrower than its natural width (contentFloor.js: its whole header label,
 * or its widest truncating value, with the cell's padding), up to its configured maximum. When the spare
 * width covers every shortfall each candidate gets its own; otherwise each gets a share of the spare width in
 * proportion to its shortfall. Width left over stays outside the table, keeping every column within its
 * bounds. Returns each candidate's extra pixels, keyed by column key.
 * @param {Array<{key: string, width: number, natural?: number, max?: number, saved?: boolean}>} columns
 * @param {number} spare
 * @returns {Object<string, number>}
 */
export const spareWidths = (columns, spare) => {
  const out = {}
  if (!(spare >= 1)) return out
  const short = []
  columns.forEach(column => {
    if (column.saved || !(column.natural > column.width)) return
    const want =
      typeof column.max === 'number'
        ? Math.min(column.natural, column.max)
        : column.natural
    const need = Math.ceil(want - column.width)
    if (need > 0) short.push({ key: column.key, need })
  })
  const total = short.reduce((sum, column) => sum + column.need, 0)
  if (!total) return out
  const budget = Math.floor(spare)
  short.forEach(({ key, need }) => {
    const extra = total <= budget ? need : Math.floor((budget * need) / total)
    if (extra > 0) out[key] = extra
  })
  return out
}

/**
 * True while any resizable column carries a width the person set, or renders at a width other than its
 * configured initial one; Reset column widths then restores the initial widths.
 */
export const widthsDiffer = (columns, view) => {
  const saved = reconcileWidths(view && view.widths, columns)
  return columns.some(
    column =>
      isResizable(column) &&
      (Object.prototype.hasOwnProperty.call(saved, column.key) ||
        widthOf(column, view) !== initialWidth(column))
  )
}

/**
 * Widths after one column changes. The width is kept even when it equals the initial one: a width the person
 * set is theirs, so spare table width (spareWidths) no longer widens that column past it.
 */
export const resizedWidths = (view, column, width) => {
  const widths = { ...((view && view.widths) || {}) }
  const next = clampWidth(column, width)
  if (next !== null) widths[column.key] = next
  return widths
}

/** Hidden keys after one column is shown or hidden. */
export const visibilityHidden = (view, key, visible) => {
  const hidden = ((view && view.hidden) || []).filter(other => other !== key)
  return visible ? hidden : hidden.concat(key)
}

// the reorderable keys in order and the positions they occupy in the full order
const reorderSlots = (order, columns) => {
  const byKey = new Map(columns.map(column => [column.key, column]))
  const slots = []
  const keys = []
  order.forEach((key, index) => {
    if (isReorderable(byKey.get(key))) {
      slots.push(index)
      keys.push(key)
    }
  })
  return { slots, keys }
}

const writeBack = (order, slots, keys) => {
  const next = order.slice()
  slots.forEach((slot, i) => {
    next[slot] = keys[i]
  })
  return next
}

/**
 * Moves a reorderable column one position (delta -1 or 1) among the reorderable columns, wrapping at
 * either end; locked and required columns keep their positions.
 */
export const moveColumn = (order, key, delta, columns) => {
  const full = reconcileOrder(order, columns)
  const { slots, keys } = reorderSlots(full, columns)
  const from = keys.indexOf(key)
  if (from < 0 || keys.length < 2) return full
  const to = (((from + delta) % keys.length) + keys.length) % keys.length
  const moved = keys.slice()
  moved.splice(from, 1)
  moved.splice(to, 0, key)
  return writeBack(full, slots, moved)
}

/** Places a reorderable column before or after another reorderable column. */
export const placeColumn = (order, key, targetKey, after, columns) => {
  const full = reconcileOrder(order, columns)
  const { slots, keys } = reorderSlots(full, columns)
  if (key === targetKey || !keys.includes(key) || !keys.includes(targetKey))
    return full
  const moved = keys.filter(other => other !== key)
  moved.splice(moved.indexOf(targetKey) + (after ? 1 : 0), 0, key)
  return writeBack(full, slots, moved)
}

/** A column's 1-based position among the reorderable columns, and their count. */
export const reorderPosition = (order, key, columns) => {
  const { keys } = reorderSlots(reconcileOrder(order, columns), columns)
  return { position: keys.indexOf(key) + 1, count: keys.length }
}
